// ---------------------------------------------------------------------------
// dataLayerClient — the ONE client-side surface for the Schema V2 data layer.
//
// Writes go through the backend /api/v2 REST routes (unified write surface —
// same auth + payloads the dl* callables had; those are now dormant). Each
// route performs the whole write atomically server-side — V2 docs
// (Session / Session.events / Encounter / EncounterNote) AND the legacy
// mirrors (Conversation / Messages / dictated_note message / Company
// counters) in one transaction — so callers never dual-write.
//
// List/detail reads go through the backend REST endpoints (same
// REACT_APP_BACKEND_URL + Firebase bearer token as every other backend call,
// via apiClient.request). Responses are V2-shaped JSON (camelCase, ISO
// timestamps); the mappers at the bottom of this file translate them into
// the legacy view-model shapes the existing components render, so component
// contracts stay stable while the transport changes underneath.
//
// All new code paths (patientService, encounterService, scribe/note flows)
// must route through this module rather than importing firebase/functions or
// hand-rolling fetches.
// ---------------------------------------------------------------------------

// (Transport unified onto backend REST routes — the dl* Firebase callables
// are dormant; see /api/v2 write routes in calliope-backend.)
import {request} from './apiClient';
import {normalizePatientMrn} from '../utils/patientMrn';

// Deterministic 1:1 encounter id the server mints for sessions that were
// never explicitly grouped (mirror of functions/src/core/constants.ts).
// A session created through the LEGACY path (old iOS, the LiveKit scribe
// agent) syncs into V2 with `encounterId = enc-{sessionId}` unless it carried
// a real `encounter_id`, so callers that only know a legacy conversation id
// can still address its V2 encounter.
export const MINTED_ENCOUNTER_PREFIX = 'enc-';
export const mintedEncounterIdFor = (sessionId: string): string =>
  `${MINTED_ENCOUNTER_PREFIX}${sessionId}`;
export const isMintedEncounterId = (encounterId: string): boolean =>
  Boolean(encounterId && encounterId.startsWith(MINTED_ENCOUNTER_PREFIX));

// ─── Session tags ⇄ legacy workflow fields ──────────────────────────────────
// Session.tags replaces the legacy workflow_type / isTelephonic / specialty /
// template_type columns. Client mirror of functions/src/core/tags.ts — keep
// the two in sync.

const WORKFLOW_TO_TAG: Record<string, string> = {
  interpretation: 'interpreter',
  medical_scribing: 'scribe',
  dictation: 'dictation',
  telehealth_interpretation: 'telehealth-interpreter',
  telehealthScribe: 'telehealth-scribe',
  telehealthInterpretation: 'telehealth-interpreter',
  live_translation: 'interpreter',
  telephonic_interpretation: 'interpreter',
};

const TAG_TO_WORKFLOW: Record<string, string> = {
  interpreter: 'interpretation',
  scribe: 'medical_scribing',
  dictation: 'dictation',
  'telehealth-interpreter': 'telehealth_interpretation',
  'telehealth-scribe': 'telehealthScribe',
  subtitle: 'interpretation',
};

const WORKFLOW_TAGS = new Set(Object.keys(TAG_TO_WORKFLOW));

export interface LegacyWorkflowFields {
  workflowType: string | null;
  isTelephonic: boolean;
  specialty: string | null;
  templateType: string | null;
}

export function legacyFieldsToTags(
  input: Partial<LegacyWorkflowFields>,
): string[] {
  const tags: string[] = [];
  const workflow = input.workflowType?.trim();
  if (workflow) tags.push(WORKFLOW_TO_TAG[workflow] ?? `workflow:${workflow}`);
  if (input.isTelephonic) tags.push('telephonic');
  if (input.specialty?.trim()) tags.push(`specialty:${input.specialty.trim()}`);
  if (input.templateType?.trim()) {
    tags.push(`template:${input.templateType.trim()}`);
  }
  return Array.from(new Set(tags));
}

export function tagsToLegacyFields(
  tags: readonly string[] = [],
): LegacyWorkflowFields {
  const workflowTag = tags.find(t => WORKFLOW_TAGS.has(t));
  const rawWorkflow = tags.find(t => t.startsWith('workflow:'));
  const stripPrefix = (prefix: string) => {
    const hit = tags.find(t => t.startsWith(prefix));
    return hit ? hit.slice(prefix.length) : null;
  };
  return {
    workflowType: workflowTag
      ? TAG_TO_WORKFLOW[workflowTag]
      : rawWorkflow
        ? rawWorkflow.slice('workflow:'.length)
        : null,
    isTelephonic: tags.includes('telephonic'),
    specialty: stripPrefix('specialty:'),
    templateType: stripPrefix('template:'),
  };
}

// ─── Write plumbing ──────────────────────────────────────────────────────────

// Write transport: the /api/v2 backend routes (same core transactions the
// callables wrapped — auth semantics and payload shapes are identical, so
// every dl* signature below is unchanged for callers).
async function writePost<TRes>(path: string, body?: unknown): Promise<TRes> {
  return request(path, {
    method: 'POST',
    ...(body !== undefined ? {body: JSON.stringify(body)} : {}),
  });
}

/**
 * True for the errors that mean "target doc isn't there (yet)" — REST 404s
 * now; the legacy callable codes are kept so pre-swap call sites that cached
 * errors keep classifying correctly.
 */
export function isDataLayerNotFound(err: unknown): boolean {
  const e = err as {code?: string; status?: number; statusCode?: number} | null;
  const code = e?.code || '';
  const status = e?.status ?? e?.statusCode;
  return (
    status === 404 || code === 'functions/not-found' || code === 'not-found'
  );
}

// ─── Write request/response types (narrow, mirroring the /api/v2 routes) ────

export type EpochMsOrIso = number | string;

export interface StartSessionRequest {
  sessionId?: string;
  /** Join/continue an existing encounter; omit to mint a 1:1 encounter. */
  encounterId?: string | null;
  tags: string[];
  status?: 'created' | 'active';
  startedAt?: EpochMsOrIso;
  patientId?: string | null;
  ehrPatientId?: string | null;
  patientName?: string | null;
  summary?: string;
  visibility?: 'private';
  livekitRoom?: string | null;
  language?: string | null;
  dialect?: string | null;
}

export interface StartSessionResponse {
  sessionId: string;
  encounterId: string;
  encounterWasCreated: boolean;
}

export type SessionEventType =
  | 'transcript.message'
  | 'translation.message'
  | 'transcript.segment_header'
  | 'scribe.note_generated'
  | 'tool.text_translated'
  | 'tool.document_translated'
  | 'tool.chart_viewed'
  | (string & {});

export interface AppendSessionEventInput {
  eventId?: string;
  sessionEventType: SessionEventType;
  occurredAt?: EpochMsOrIso;
  payload: Record<string, unknown>;
}

export interface AppendSessionEventsResponse {
  sessionId: string;
  eventIds: string[];
  firstSequence: number;
}

export interface EndSessionRequest {
  sessionId: string;
  outcome?: 'ended' | 'failed';
  endedAt?: EpochMsOrIso;
  durationSeconds?: number;
  /** Active (idle-excluded) minutes — the billable quantity when tracked. */
  billableMinutes?: number;
  summary?: string;
  failureReason?: string;
}

export interface EndSessionResponse {
  sessionId: string;
  status: string;
  durationSeconds: number;
}

export interface SetEncounterPatientRequest {
  encounterId: string;
  patientId: string | null;
  ehrPatientId?: string | null;
  patientName?: string | null;
}

export interface CreateEncounterNoteSectionInput {
  key: string;
  title: string;
  content: string;
  order: number;
  templateSectionId?: string | null;
}

export interface CreateEncounterNoteRequest {
  encounterId: string;
  templateId?: string | null;
  sourceSessionIds: string[];
  sections: CreateEncounterNoteSectionInput[];
}

export interface CreateEncounterNoteResponse {
  noteId: string;
  sectionIds: string[];
}

export interface UpsertPatientFields {
  firstName: string;
  lastName: string;
  preferredLanguage: string | null;
  preferredDialect: string | null;
  /** 'YYYY-MM-DD' or null. */
  dateOfBirth: string | null;
  gender: string | null;
  phoneNumber: string | null;
  email: string | null;
  mrn: string | null;
  notes: string | null;
}

export interface UpsertPatientRequest {
  patientId?: string;
  fields: UpsertPatientFields;
  ehrLink?: {ehrType: string; externalPatientId: string};
}

export interface UpsertPatientResponse {
  patientId: string;
  patientEHRLinkId: string | null;
}

// ─── The write wrappers (backend /api/v2 routes) ─────────────────────────────
// Caller identity / company / facility are resolved server-side from the
// Firebase bearer token — never passed from the client. Signatures are
// unchanged from the callable era, so call sites are transport-agnostic.

export const dlStartSession = (
  data: StartSessionRequest,
): Promise<StartSessionResponse> => writePost('/api/v2/sessions', data);

export const dlAppendSessionEvents = ({
  sessionId,
  events,
}: {
  sessionId: string;
  events: AppendSessionEventInput[];
}): Promise<AppendSessionEventsResponse> =>
  writePost(`/api/v2/sessions/${encodeURIComponent(sessionId)}/events`, {
    events,
  });

export const dlEndSession = ({
  sessionId,
  ...body
}: EndSessionRequest): Promise<EndSessionResponse> =>
  writePost(`/api/v2/sessions/${encodeURIComponent(sessionId)}/end`, body);

export const dlRateSession = async ({
  sessionId,
  ...body
}: {
  sessionId: string;
  rating: number;
  ratingReason?: string | null;
}): Promise<{ok: boolean}> => {
  await writePost(
    `/api/v2/sessions/${encodeURIComponent(sessionId)}/rate`,
    body,
  );
  return {ok: true};
};

export const dlSetEncounterPatient = async ({
  encounterId,
  ...body
}: SetEncounterPatientRequest): Promise<{ok: boolean}> => {
  await writePost(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/patient`,
    body,
  );
  return {ok: true};
};

export const dlCreateEncounterNote = (
  data: CreateEncounterNoteRequest,
): Promise<CreateEncounterNoteResponse> => writePost('/api/v2/notes', data);

export const dlUpdateNoteSection = async ({
  noteId,
  sectionKey,
  content,
}: {
  noteId: string;
  sectionKey: string;
  content: string;
}): Promise<{ok: boolean}> => {
  await request(
    `/api/v2/notes/${encodeURIComponent(noteId)}/sections/${encodeURIComponent(sectionKey)}`,
    {method: 'PATCH', body: JSON.stringify({content})},
  );
  return {ok: true};
};

export const dlMarkNotePushed = async ({
  noteId,
}: {
  noteId: string;
}): Promise<{ok: boolean}> => {
  await writePost(`/api/v2/notes/${encodeURIComponent(noteId)}/mark-pushed`);
  return {ok: true};
};

export const dlSoftDeleteSession = async ({
  sessionId,
}: {
  sessionId: string;
}): Promise<{ok: boolean}> => {
  await request(`/api/v2/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
  return {ok: true};
};

export const dlSoftDeleteEncounter = async ({
  encounterId,
}: {
  encounterId: string;
}): Promise<{ok: boolean}> => {
  await request(`/api/v2/encounters/${encodeURIComponent(encounterId)}`, {
    method: 'DELETE',
  });
  return {ok: true};
};

export const dlUpsertPatient = (
  data: UpsertPatientRequest,
): Promise<UpsertPatientResponse> =>
  request('/api/v2/patients', {method: 'PUT', body: JSON.stringify(data)});

/**
 * DELETE /api/v2/patients/:patientId — company-checked server-side; deletes
 * the Patient doc AND its PatientEHRLink in one transaction (parity with the
 * legacy client deletePatient, minus the per-user Conversation unlink stamps,
 * which are retired — patient identity now lives on the Encounter).
 */
export const dlDeletePatient = async ({
  patientId,
}: {
  patientId: string;
}): Promise<{ok: boolean}> => {
  await request(`/api/v2/patients/${encodeURIComponent(patientId)}`, {
    method: 'DELETE',
  });
  return {ok: true};
};

/**
 * POST /api/v2/sessions/:sessionId/restore — un-soft-deletes a session:
 * isRemoved=false, status recomputed, encounter aggregates refreshed, legacy
 * `is_removed` mirror cleared. Session-access-gated (owner/admin).
 */
export const dlRestoreSession = async ({
  sessionId,
}: {
  sessionId: string;
}): Promise<{ok: boolean}> => {
  await writePost(`/api/v2/sessions/${encodeURIComponent(sessionId)}/restore`);
  return {ok: true};
};

/**
 * PATCH /api/v2/encounters/:encounterId/summary — sets the encounter
 * projection summary and mirrors it onto each member session's legacy
 * Conversation doc (skipping missing docs). Encounter-access-gated.
 */
export const dlSetEncounterSummary = async ({
  encounterId,
  summary,
}: {
  encounterId: string;
  summary: string;
}): Promise<{ok: boolean}> => {
  await request(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/summary`,
    {method: 'PATCH', body: JSON.stringify({summary})},
  );
  return {ok: true};
};

/**
 * PATCH /api/v2/encounters/:encounterId/language — stamps the interpretation
 * language (+ optional dialect) onto every member session's V2 doc and legacy
 * Conversation mirror. Used by the Continue flow when a closed encounter is
 * resumed as a live-translation session but never captured a language, so the
 * choice is persisted and future continuations resolve it without prompting.
 * Encounter-access-gated.
 */
export const dlSetEncounterLanguage = async ({
  encounterId,
  language,
  dialect = null,
}: {
  encounterId: string;
  language: string;
  dialect?: string | null;
}): Promise<{ok: boolean}> => {
  await request(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/language`,
    {method: 'PATCH', body: JSON.stringify({language, dialect})},
  );
  return {ok: true};
};

/**
 * POST /api/v2/encounters/:encounterId/adopt-session {sessionId} — moves an
 * EXISTING session out of its current encounter (usually its minted
 * `enc-{sessionId}` twin) into the target encounter, updating aggregates on
 * both sides and stamping `encounter_id` on the legacy Conversation doc.
 * Caller must have access to both the encounter and the session. This is the
 * server-side primitive behind "combine into one encounter" — joining a NEW
 * session is still dlStartSession({encounterId}).
 */
export const dlAdoptSessionIntoEncounter = ({
  encounterId,
  sessionId,
}: {
  encounterId: string;
  sessionId: string;
}): Promise<{sessionId: string; encounterId: string}> =>
  writePost(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/adopt-session`,
    {sessionId},
  );

/**
 * POST /api/v2/encounters/:encounterId/detach-session {sessionId} — "ungroup":
 * moves the session out of this encounter back to standalone (its minted
 * `enc-{sessionId}` twin), recomputing aggregates on both sides. Inverse of
 * adopt-session. The server 400s when the session isn't currently a member of
 * :encounterId (stale client state — nothing to detach).
 */
export const dlDetachSessionFromEncounter = ({
  encounterId,
  sessionId,
}: {
  encounterId: string;
  sessionId: string;
}): Promise<{sessionId: string; encounterId: string}> =>
  writePost(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/detach-session`,
    {sessionId},
  );

/**
 * POST /api/v2/encounters/:encounterId/close {reason?} — "mark done":
 * force-closes every member session still created/active (crashed recordings
 * that never got an end) and flips the encounter's status projection. Billing
 * is deliberately untouched. `reason` lands on the legacy `closed_reason`.
 */
export const dlCloseEncounter = async ({
  encounterId,
  reason,
}: {
  encounterId: string;
  reason?: string;
}): Promise<{ok: boolean}> => {
  await writePost(
    `/api/v2/encounters/${encodeURIComponent(encounterId)}/close`,
    reason ? {reason} : {},
  );
  return {ok: true};
};

/**
 * POST /api/v2/sessions/:sessionId/tags {add?, remove?} — post-start tag
 * mutation (e.g. the deterministic `telephonic` stamp on web dial-out). The
 * server merges the tag set and recomputes the legacy mirror columns
 * (workflow_type / isTelephonic / specialty / template_type).
 */
export const dlUpdateSessionTags = ({
  sessionId,
  add,
  remove,
}: {
  sessionId: string;
  add?: string[];
  remove?: string[];
}): Promise<{sessionId: string; tags: string[]}> =>
  writePost(`/api/v2/sessions/${encodeURIComponent(sessionId)}/tags`, {
    ...(add?.length ? {add} : {}),
    ...(remove?.length ? {remove} : {}),
  });

// ─── REST read types ─────────────────────────────────────────────────────────

export interface RecentSessionEncounterContext {
  patientId: string | null;
  patientName: string | null;
  summary: string;
  sessionsNum: number;
  visibility: string | null;
  encounterNoteIds: string[];
}

export interface RecentSessionRow {
  sessionId: string;
  encounterId: string | null;
  providerUserId: string | null;
  status: string | null;
  tags: string[];
  language: string | null;
  dialect: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  rating: number | null;
  recordingIds: string[];
  encounter: RecentSessionEncounterContext | null;
}

export interface SessionEventRow {
  eventId: string;
  sessionEventType: SessionEventType;
  sequence: number;
  occurredAt: string | null;
  payload: Record<string, unknown>;
}

export interface SessionEventsResponse {
  sessionId: string;
  encounterId: string | null;
  status: string | null;
  tags: string[];
  language: string | null;
  events: SessionEventRow[];
}

export interface EncounterSessionSummary {
  sessionId: string;
  providerUserId?: string | null;
  status: string | null;
  startedAt: string | null;
  endedAt?: string | null;
  durationSeconds: number;
  tags: string[];
}

export interface EncounterNoteSectionRow {
  sectionId: string;
  key: string;
  title: string;
  content: string;
  order: number;
  lastEditedAt: string | null;
  lastEditedBy: string | null;
}

export interface EncounterNoteRow {
  noteId: string;
  providerUserId: string | null;
  templateId: string | null;
  pushedToEhr: boolean;
  ehrPushedAt: string | null;
  sourceSessionIds: string[];
  sectionIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  sections?: EncounterNoteSectionRow[];
}

export interface EncounterDetailResponse {
  encounterId: string;
  patientId: string | null;
  patientName: string | null;
  summary: string;
  visibility: string | null;
  providerUserIds: string[];
  sessionsNum: number;
  sessionsTotalMin: number;
  createdAt: string | null;
  updatedAt: string | null;
  sessions: EncounterSessionSummary[];
  notes: EncounterNoteRow[];
}

export interface PatientEncounterRow {
  encounterId: string;
  summary: string;
  sessionsNum: number;
  sessionsTotalMin: number;
  providerUserIds: string[];
  encounterNoteIds: string[];
  createdAt: string | null;
  updatedAt: string | null;
  sessions: EncounterSessionSummary[];
}

/**
 * GET /api/v2/encounters rows — same core shape as the patient-history rows,
 * plus the patient identity/visibility columns the company-wide browse
 * renders (the patient rows omit them because the caller queried by patient).
 */
export interface CompanyEncounterRow extends PatientEncounterRow {
  patientId?: string | null;
  ehrPatientId?: string | null;
  patientName?: string | null;
  visibility?: string | null;
}

/** GET /api/v2/patients roster row (camelCase, ISO timestamps). The same
 *  shape is served by the single GET, POST /lookup and GET /recent — those
 *  rows additionally carry notes / createdBy / the external `ehrPatientId`
 *  (doc-level legacy mirror, upgraded from the PatientEHRLink where known). */
export interface PatientRosterRow {
  patientId: string;
  firstName: string | null;
  lastName: string | null;
  preferredLanguage: string | null;
  preferredDialect: string | null;
  /** 'YYYY-MM-DD' or null. */
  dateOfBirth: string | null;
  gender: string | null;
  mrn: string | null;
  phoneNumber: string | null;
  email: string | null;
  patientEHRLinkId: string | null;
  notes?: string | null;
  createdBy?: string | null;
  /** External EHR patient id (bare source id) when the patient is linked. */
  ehrPatientId?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** GET /api/v2/patients/recent row — roster row + last-seen stamp. */
export interface RecentPatientRow extends PatientRosterRow {
  lastSessionAt: string | null;
}

/** PatientDocument row (snake_case, ISO timestamps — the web's historical
 *  direct-Firestore doc shape served over REST). */
export interface PatientDocumentRow {
  id: string;
  patient_id: string | null;
  company_id: string | null;
  facility_id: string | null;
  uploaded_by: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number;
  storage_path: string | null;
  storage_url: string;
  document_type: string;
  extracted_text: string;
  extraction_status: string;
  extraction_error: string | null;
  page_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── REST reads (existing apiClient → REACT_APP_BACKEND_URL + bearer token) ──

export async function fetchRecentSessions({
  scope = 'mine',
  limit = 20,
  startDate,
  endDate,
  signal,
}: {
  scope?: 'mine' | 'facility' | 'company';
  limit?: number;
  startDate?: string | null;
  endDate?: string | null;
  signal?: AbortSignal;
} = {}): Promise<RecentSessionRow[]> {
  const data = await request('/api/v1/sessions/recent', {
    query: {
      scope,
      limit,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    },
    signal,
  });
  return Array.isArray(data?.sessions) ? data.sessions : [];
}

export async function fetchSessionEvents(
  sessionId: string,
  {signal}: {signal?: AbortSignal} = {},
): Promise<SessionEventsResponse> {
  return request(`/api/v1/sessions/${encodeURIComponent(sessionId)}/events`, {
    signal,
  });
}

export async function fetchEncounterDetail(
  encounterId: string,
  {
    includeSections = true,
    signal,
  }: {includeSections?: boolean; signal?: AbortSignal} = {},
): Promise<EncounterDetailResponse> {
  return request(`/api/v1/encounters/${encodeURIComponent(encounterId)}`, {
    query: {includeSections},
    signal,
  });
}

export async function fetchPatientEncounters(
  patientId: string,
  {limit = 20, signal}: {limit?: number; signal?: AbortSignal} = {},
): Promise<PatientEncounterRow[]> {
  const data = await request(
    `/api/v1/patients/${encodeURIComponent(patientId)}/encounters`,
    {query: {limit}, signal},
  );
  return Array.isArray(data?.encounters) ? data.encounters : [];
}

/**
 * GET /api/v2/patients — the company-scoped patient roster, ordered by
 * `updatedAt` desc with cursor pagination (`cursor` = the last row's
 * updatedAt ISO string, echoed back as `nextCursor`).
 *
 * `q` is a NAIVE case-insensitive substring filter on firstName/lastName
 * applied server-side over the fetched window only (parity with the client
 * filter it replaces) — it is NOT a full-roster search: rows older than the
 * current window are only found by paging further.
 */
export async function fetchPatientsPage({
  limit = 50,
  cursor,
  q,
  signal,
}: {
  limit?: number;
  cursor?: string | null;
  q?: string | null;
  signal?: AbortSignal;
} = {}): Promise<{patients: PatientRosterRow[]; nextCursor: string | null}> {
  const data = await request('/api/v2/patients', {
    query: {limit, cursor: cursor || undefined, q: q || undefined},
    signal,
  });
  return {
    patients: Array.isArray(data?.patients) ? data.patients : [],
    nextCursor: data?.nextCursor || null,
  };
}

/**
 * GET /api/v2/patients/:patientId — company-gated single patient: the roster
 * row shape plus notes / createdBy / the external EHR id (canonical
 * PatientEHRLink value preferred over the doc-level mirror). Returns null on
 * 404 so callers keep the legacy "missing doc" contract.
 */
export async function fetchPatientRow(
  patientId: string,
  {signal}: {signal?: AbortSignal} = {},
): Promise<PatientRosterRow | null> {
  try {
    return await request(`/api/v2/patients/${encodeURIComponent(patientId)}`, {
      signal,
    });
  } catch (err) {
    if (isDataLayerNotFound(err)) return null;
    throw err;
  }
}

/**
 * POST /api/v2/patients/lookup — bulk hydration by explicit ids (recents /
 * merge / planner hydration flows). Both arms are company-scoped server-side;
 * missing/foreign ids are silently omitted (absence = "not found").
 * `ehrPatientIds` resolve through PatientEHRLink (companyId +
 * externalPatientId), which also covers legacy free-form ehrType links.
 */
export async function lookupPatientRows({
  patientIds,
  ehrPatientIds,
  signal,
}: {
  patientIds?: string[];
  ehrPatientIds?: string[];
  signal?: AbortSignal;
}): Promise<PatientRosterRow[]> {
  if (!patientIds?.length && !ehrPatientIds?.length) return [];
  const data = await request('/api/v2/patients/lookup', {
    method: 'POST',
    body: JSON.stringify({
      ...(patientIds?.length ? {patientIds} : {}),
      ...(ehrPatientIds?.length ? {ehrPatientIds} : {}),
    }),
    signal,
  });
  return Array.isArray(data?.patients) ? data.patients : [];
}

/**
 * GET /api/v2/patients/recent — the caller's recently-seen patients (their
 * newest sessions walked to encounters, deduped by patient, hydrated), for
 * the "Recent patients" picker. WINDOWED: only the newest ~limit*6 sessions
 * are scanned, matching the legacy client's last-60-conversations scan.
 */
export async function fetchRecentPatientRows({
  limit = 10,
  signal,
}: {limit?: number; signal?: AbortSignal} = {}): Promise<RecentPatientRow[]> {
  const data = await request('/api/v2/patients/recent', {
    query: {limit},
    signal,
  });
  return Array.isArray(data?.patients) ? data.patients : [];
}

/**
 * GET /api/v2/sessions/:sessionId — single-session METADATA (no transcript):
 * exactly one /api/v1/sessions/recent row including the decorated encounter
 * block. Returns null on 404 (missing doc) so callers keep the legacy
 * getConversation contract.
 */
export async function fetchSessionRow(
  sessionId: string,
  {signal}: {signal?: AbortSignal} = {},
): Promise<RecentSessionRow | null> {
  try {
    return await request(`/api/v2/sessions/${encodeURIComponent(sessionId)}`, {
      signal,
    });
  } catch (err) {
    if (isDataLayerNotFound(err)) return null;
    throw err;
  }
}

// ─── Patient documents (PDF uploads for scribe context) ─────────────────────
// Metadata CRUD over REST; the Storage object itself stays client-managed
// (create is metadata-before-upload, delete is metadata-only — callers delete
// the Storage file separately, exactly like the direct-Firestore era).

export async function fetchPatientDocumentRows(
  patientId: string,
  {limit, signal}: {limit?: number; signal?: AbortSignal} = {},
): Promise<PatientDocumentRow[]> {
  const data = await request(
    `/api/v2/patients/${encodeURIComponent(patientId)}/documents`,
    {query: limit ? {limit} : undefined, signal},
  );
  return Array.isArray(data?.documents) ? data.documents : [];
}

export async function createPatientDocumentRow(
  patientId: string,
  body: {
    fileName: string;
    fileType?: string;
    fileSize?: number;
    storagePath: string;
    storageUrl?: string;
    documentType?: string;
  },
): Promise<PatientDocumentRow> {
  const data = await request(
    `/api/v2/patients/${encodeURIComponent(patientId)}/documents`,
    {method: 'POST', body: JSON.stringify(body)},
  );
  return data?.document;
}

export async function deletePatientDocumentRow(
  patientId: string,
  documentId: string,
): Promise<{ok: boolean}> {
  await request(
    `/api/v2/patients/${encodeURIComponent(patientId)}/documents/${encodeURIComponent(documentId)}`,
    {method: 'DELETE'},
  );
  return {ok: true};
}

/**
 * GET /api/v2/sessions/removed — the caller's own soft-deleted sessions
 * (server-filtered `providerUserId == uid AND isRemoved`), same row shape as
 * /api/v1/sessions/recent. Feeds the "Removed sessions" trash view; restore
 * goes through dlRestoreSession.
 */
export async function fetchRemovedSessions({
  limit = 50,
  startDate,
  endDate,
  signal,
}: {
  limit?: number;
  startDate?: string | null;
  endDate?: string | null;
  signal?: AbortSignal;
} = {}): Promise<RecentSessionRow[]> {
  const data = await request('/api/v2/sessions/removed', {
    query: {
      limit,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    },
    signal,
  });
  return Array.isArray(data?.sessions) ? data.sessions : [];
}

/**
 * GET /api/v2/encounters — company-wide encounter browse (companyId +
 * updatedAt desc index, cursor = last row's updatedAt ISO echoed back as
 * `nextCursor`), with the session-privacy visibility filter applied
 * server-side (private encounters hidden unless contributor/admin).
 *
 * `q` is a WINDOWED substring filter: a naive case-insensitive match over
 * patient_name/summary applied to the fetched window only — exactly the
 * semantics of the client-side `search_tokens` filter it replaces, not a
 * full-history text index. Callers page further to search older history.
 *
 * `ehrPatientId` (exact match on the legacy `ehr_patient_id` mirror) has the
 * same in-window semantics: only the newest ~200 encounters are scanned, so
 * an EHR patient last seen further back returns empty — timeline merges
 * tolerate the miss.
 */
export async function fetchCompanyEncounters({
  limit = 30,
  cursor,
  q,
  ehrPatientId,
  signal,
}: {
  limit?: number;
  cursor?: string | null;
  q?: string | null;
  ehrPatientId?: string | null;
  signal?: AbortSignal;
} = {}): Promise<{
  encounters: CompanyEncounterRow[];
  nextCursor: string | null;
}> {
  const data = await request('/api/v2/encounters', {
    query: {
      limit,
      cursor: cursor || undefined,
      q: q || undefined,
      ehrPatientId: ehrPatientId || undefined,
    },
    signal,
  });
  return {
    encounters: Array.isArray(data?.encounters) ? data.encounters : [],
    nextCursor: data?.nextCursor || null,
  };
}

// ─── Mappers: V2 wire shapes → legacy view-models ────────────────────────────
// The components (SessionDetailView, planner cards, PostSessionNotesView …)
// render the legacy Firestore shapes. These mappers keep those props stable.

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/**
 * V2 SessionEvent → legacy Message doc shape (client mirror of the server's
 * eventToLegacyMessage adapter, with ISO timestamps hydrated to Dates so the
 * existing `normalizeTimestamp` / `toDate?.()` call sites keep working).
 */
export function sessionEventToLegacyMessage(
  event: SessionEventRow,
): Record<string, any> {
  const p = event.payload || {};
  const speakerRole = str(p.speakerRole);
  const isDoctor = speakerRole === 'provider';
  const type = event.sessionEventType;

  const base: Record<string, any> = {
    id: event.eventId,
    timestamp: toDate(event.occurredAt) || new Date(0),
    is_doctor: isDoctor,
    is_agent_message: p.isAgentMessage === true,
    model: str(p.model) || '',
    quality_check_state: str(p.qualityCheckState) || 'complete',
  };
  if (str(p.speakerId) !== null) base.speaker_id = str(p.speakerId);
  if (typeof p.confidenceScore === 'number') {
    base.confidence_score = p.confidenceScore;
  }
  if (str(p.explicitMeaning)) base.explicit_meaning = str(p.explicitMeaning);
  if (Array.isArray(p.ignoredSegments) && p.ignoredSegments.length > 0) {
    base.ignored_segments = p.ignoredSegments;
  }

  if (type === 'translation.message') {
    return {
      ...base,
      content_type: 'translation',
      input_text: str(p.inputText) || '',
      output_text: str(p.outputText) || '',
      from_language: str(p.fromLanguage) || '',
      to_language: str(p.toLanguage) || '',
    };
  }

  if (type === 'scribe.note_generated') {
    const structured: Record<string, any> = {
      note_type: str(p.noteType) || str(p.templateId) || 'soap',
      sections: p.sections && typeof p.sections === 'object' ? p.sections : {},
      template_id: str(p.templateId) || str(p.noteType) || 'soap',
      is_complete: p.isComplete !== false,
    };
    const encounterNoteId = str(p.encounterNoteId);
    if (encounterNoteId) structured.encounter_note_id = encounterNoteId;
    const text = str(p.text) || '';
    return {
      ...base,
      content_type: 'dictated_note',
      input_text: text,
      output_text: text,
      from_language: 'English',
      to_language: 'English',
      structured_output: structured,
    };
  }

  // transcript.message / transcript.segment_header / anything else
  const text = str(p.text) || '';
  return {
    ...base,
    content_type:
      type === 'transcript.segment_header' ? 'segment_header' : 'transcription',
    input_text: text,
    output_text: text,
    transcript: text,
    is_user: !isDoctor,
    from_language: str(p.language) || 'English',
    to_language: str(p.language) || 'English',
  };
}

export function sessionEventsToLegacyMessages(
  events: SessionEventRow[] = [],
): Record<string, any>[] {
  return [...events]
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map(sessionEventToLegacyMessage);
}

/**
 * One recent-sessions row → the pseudo-Conversation object the legacy
 * grouping helpers and cards read (`start_time`/`end_time` as Dates —
 * `toJsDate` accepts them — plus the denormalized patient/workflow fields).
 */
export function recentSessionRowToLegacyConversation(
  row: RecentSessionRow,
): Record<string, any> {
  const legacy = tagsToLegacyFields(row.tags || []);
  const startTime = toDate(row.startedAt);
  const endTime = toDate(row.endedAt);
  const open = row.status === 'active' || row.status === 'created';
  return {
    id: row.sessionId,
    user_id: row.providerUserId || null,
    encounter_id: row.encounterId || null,
    patient_id: row.encounter?.patientId ?? null,
    patient_name: row.encounter?.patientName ?? '',
    summary: row.encounter?.summary ?? '',
    workflow_type: legacy.workflowType || 'medical_scribing',
    template_type: legacy.templateType || 'soap',
    specialty: legacy.specialty || '',
    ...(legacy.isTelephonic ? {isTelephonic: true} : {}),
    language: row.language || null,
    dialect: row.dialect || null,
    start_time: startTime,
    end_time: open ? null : endTime,
    minutes: Math.ceil((row.durationSeconds || 0) / 60),
    wall_clock_seconds: row.durationSeconds || 0,
    rating: row.rating ?? null,
    is_removed: false,
    _v2SessionStatus: row.status || null,
  };
}

/**
 * GET /sessions/recent rows → EncounterView[] (the shape every list surface
 * renders). Rows are grouped by their V2 `encounterId`; each group becomes
 * one view whose member `conversations` are the mapped session rows.
 */
export function recentSessionsToEncounterViews(
  rows: RecentSessionRow[] = [],
  {companyId = null}: {companyId?: string | null} = {},
): Record<string, any>[] {
  const byEncounter = new Map<
    string,
    {rows: RecentSessionRow[]; context: RecentSessionEncounterContext | null}
  >();
  for (const row of rows) {
    if (!row?.sessionId) continue;
    const encounterId = row.encounterId || mintedEncounterIdFor(row.sessionId);
    const bucket = byEncounter.get(encounterId) || {rows: [], context: null};
    bucket.rows.push(row);
    if (!bucket.context && row.encounter) bucket.context = row.encounter;
    byEncounter.set(encounterId, bucket);
  }

  const views: Record<string, any>[] = [];
  for (const [encounterId, {rows: memberRows, context}] of byEncounter) {
    const conversations = memberRows
      .map(recentSessionRowToLegacyConversation)
      .sort(
        (a, b) =>
          (a.start_time?.getTime?.() ?? 0) - (b.start_time?.getTime?.() ?? 0),
      );
    const hasOpen = conversations.some(c => !c.end_time);
    const startTimes = conversations
      .map(c => c.start_time)
      .filter(Boolean) as Date[];
    const endTimes = conversations
      .map(c => c.end_time)
      .filter(Boolean) as Date[];
    const first = conversations[0] || {};
    const contributorIds = Array.from(
      new Set(conversations.map(c => c.user_id).filter(Boolean)),
    );

    views.push({
      id: encounterId,
      // Minted 1:1 encounters are REAL V2 docs — "continue" can join them
      // directly via dlStartSession({encounterId}) without materializing.
      isSynthetic: false,
      isTelephonic: conversations.some(c => c.isTelephonic === true),
      patient_id: context?.patientId ?? null,
      // Not exposed by /sessions/recent; hydration falls back to the local
      // patient's linked EHR id (see plannerEncounterHydration bucket B).
      ehr_patient_id: null,
      patient_name: context?.patientName || '',
      company_id: companyId || null,
      facility_id: '',
      workflow_type: first.workflow_type || 'medical_scribing',
      template_type: first.template_type || 'soap',
      specialty: first.specialty || '',
      foreign_language: first.language || '',
      contributors: contributorIds.map(userId => {
        const memberConvs = conversations.filter(c => c.user_id === userId);
        return {
          user_id: userId,
          provider_name: null,
          conversation_count: memberConvs.length,
          total_minutes: memberConvs.reduce(
            (sum, c) => sum + (Number(c.minutes) || 0),
            0,
          ),
        };
      }),
      contributor_user_ids: contributorIds,
      conversation_ids: conversations.map(c => c.id),
      conversations,
      status: hasOpen ? 'open' : 'closed',
      start_time: startTimes.length
        ? new Date(Math.min(...startTimes.map(d => d.getTime())))
        : null,
      end_time:
        hasOpen || endTimes.length === 0
          ? null
          : new Date(Math.max(...endTimes.map(d => d.getTime()))),
      total_minutes: conversations.reduce(
        (sum, c) => sum + (Number(c.minutes) || 0),
        0,
      ),
      summary: context?.summary || '',
      is_removed: false,
      has_open_session: hasOpen,
      visibility: context?.visibility || null,
      encounterNoteIds: context?.encounterNoteIds || [],
    });
  }

  return views.sort(
    (a, b) =>
      (b.start_time?.getTime?.() ?? 0) - (a.start_time?.getTime?.() ?? 0),
  );
}

/**
 * GET /patients/:id/encounters rows → legacy Encounter-doc-shaped objects
 * (what fetchEncountersForPatient callers and the grouping helpers expect).
 * The caller supplies the patient identity it queried with — the REST rows
 * are already patient-scoped and don't repeat it.
 */
export function patientEncounterRowToLegacyEncounterDoc(
  row: PatientEncounterRow,
  {
    patientId = null,
    ehrPatientId = null,
    patientName = '',
    companyId = null,
  }: Record<string, any> = {},
): Record<string, any> {
  const sessions = Array.isArray(row.sessions) ? row.sessions : [];
  const anyOpen = sessions.some(
    s => s.status === 'active' || s.status === 'created',
  );
  const startTimes = sessions
    .map(s => toDate(s.startedAt))
    .filter(Boolean) as Date[];
  const firstTags = sessions[0]?.tags || [];
  const legacy = tagsToLegacyFields(firstTags);
  const startTime = startTimes.length
    ? new Date(Math.min(...startTimes.map(d => d.getTime())))
    : toDate(row.createdAt);
  return {
    id: row.encounterId,
    patient_id: patientId,
    ehr_patient_id: ehrPatientId,
    patient_name: patientName || '',
    company_id: companyId,
    facility_id: '',
    contributor_user_ids: row.providerUserIds || [],
    conversation_ids: sessions.map(s => s.sessionId).filter(Boolean),
    workflow_type: legacy.workflowType || 'medical_scribing',
    template_type: legacy.templateType || 'soap',
    specialty: legacy.specialty || '',
    status: anyOpen ? 'open' : 'closed',
    start_time: startTime,
    end_time: anyOpen ? null : toDate(row.updatedAt),
    total_minutes: Number(row.sessionsTotalMin) || 0,
    summary: row.summary || '',
    is_removed: false,
    created_at: toDate(row.createdAt),
    updated_at: toDate(row.updatedAt),
    encounterNoteIds: row.encounterNoteIds || [],
    _v2: true,
    // Raw member-session summaries, carried so timeline surfaces can build
    // per-session conversation rows without a per-encounter detail fetch.
    _v2Sessions: sessions,
  };
}

/**
 * GET /api/v2/encounters row → legacy Encounter-doc shape. Same mapping as
 * the patient-history rows, with the patient identity read off the row
 * itself (the company browse isn't patient-scoped).
 */
export function companyEncounterRowToLegacyEncounterDoc(
  row: CompanyEncounterRow,
  {companyId = null}: {companyId?: string | null} = {},
): Record<string, any> {
  const doc = patientEncounterRowToLegacyEncounterDoc(row, {
    patientId: row.patientId ?? null,
    ehrPatientId: row.ehrPatientId ?? null,
    patientName: row.patientName || '',
    companyId,
  });
  if (row.visibility) doc.visibility = row.visibility;
  return doc;
}

/**
 * One EncounterSessionSummary (a member-session row embedded in the REST
 * encounter reads) → the legacy Conversation shape the grouping helpers and
 * timeline cards render. Patient identity comes from the parent encounter —
 * V2 stores it once, on the Encounter.
 */
export function encounterSessionSummaryToLegacyConversation(
  session: EncounterSessionSummary,
  {
    encounterId = null,
    patientId = null,
    ehrPatientId = null,
    patientName = '',
    summary = '',
    companyId = null,
  }: Record<string, any> = {},
): Record<string, any> {
  const legacy = tagsToLegacyFields(session.tags || []);
  const startTime = toDate(session.startedAt);
  const endTime = toDate(session.endedAt ?? null);
  const open = session.status === 'active' || session.status === 'created';
  return {
    id: session.sessionId,
    user_id: session.providerUserId ?? null,
    encounter_id: encounterId,
    patient_id: patientId,
    ehr_patient_id: ehrPatientId,
    patient_name: patientName || '',
    company_id: companyId,
    summary: summary || '',
    workflow_type: legacy.workflowType || 'medical_scribing',
    template_type: legacy.templateType || 'soap',
    specialty: legacy.specialty || '',
    ...(legacy.isTelephonic ? {isTelephonic: true} : {}),
    start_time: startTime,
    end_time: open ? null : endTime,
    minutes: Math.ceil((session.durationSeconds || 0) / 60),
    wall_clock_seconds: session.durationSeconds || 0,
    is_removed: false,
    _v2SessionStatus: session.status || null,
  };
}

/**
 * V2 roster row → the legacy snake_case Patient doc shape every picker /
 * roster surface renders (mirror of the iOS CodingKeys). Rows served by the
 * single GET / lookup / recent endpoints also carry notes, createdBy and the
 * external EHR id — mapped onto the legacy `notes` / `created_by` /
 * `fhir_patient_id` fields so EHR-link resolution (resolveEhrPatientId,
 * linkedEhrPatientId) keeps reading its historical field names.
 */
export function patientRosterRowToLegacyPatientDoc(
  row: PatientRosterRow,
): Record<string, any> {
  return {
    id: row.patientId,
    first_name: row.firstName || '',
    last_name: row.lastName || '',
    preferred_language: row.preferredLanguage ?? null,
    preferred_dialect: row.preferredDialect ?? null,
    date_of_birth: row.dateOfBirth ?? null,
    gender: row.gender ?? null,
    mrn: normalizePatientMrn(row.mrn) || null,
    phone_number: row.phoneNumber ?? null,
    email: row.email ?? null,
    patientEHRLinkId: row.patientEHRLinkId ?? null,
    ...(row.notes !== undefined ? {notes: row.notes ?? null} : {}),
    ...(row.createdBy !== undefined ? {created_by: row.createdBy ?? null} : {}),
    ...(row.ehrPatientId ? {fhir_patient_id: row.ehrPatientId} : {}),
    created_at: toDate(row.createdAt),
    updated_at: toDate(row.updatedAt),
    _v2: true,
  };
}

const dataLayerClient = {
  // callables
  dlStartSession,
  dlAppendSessionEvents,
  dlEndSession,
  dlRateSession,
  dlSetEncounterPatient,
  dlCreateEncounterNote,
  dlUpdateNoteSection,
  dlMarkNotePushed,
  dlSoftDeleteSession,
  dlSoftDeleteEncounter,
  dlUpsertPatient,
  dlDeletePatient,
  dlRestoreSession,
  dlSetEncounterSummary,
  dlSetEncounterLanguage,
  dlAdoptSessionIntoEncounter,
  dlDetachSessionFromEncounter,
  dlCloseEncounter,
  dlUpdateSessionTags,
  // REST reads
  fetchRecentSessions,
  fetchSessionEvents,
  fetchEncounterDetail,
  fetchPatientEncounters,
  fetchPatientsPage,
  fetchPatientRow,
  lookupPatientRows,
  fetchRecentPatientRows,
  fetchSessionRow,
  fetchPatientDocumentRows,
  createPatientDocumentRow,
  deletePatientDocumentRow,
  fetchRemovedSessions,
  fetchCompanyEncounters,
  // helpers/mappers
  mintedEncounterIdFor,
  isMintedEncounterId,
  isDataLayerNotFound,
  legacyFieldsToTags,
  tagsToLegacyFields,
  sessionEventToLegacyMessage,
  sessionEventsToLegacyMessages,
  recentSessionRowToLegacyConversation,
  recentSessionsToEncounterViews,
  patientEncounterRowToLegacyEncounterDoc,
  companyEncounterRowToLegacyEncounterDoc,
  encounterSessionSummaryToLegacyConversation,
  patientRosterRowToLegacyPatientDoc,
};

export default dataLayerClient;
